import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Paper, Typography, CircularProgress, Button } from '@mui/material';
import AdminDataGrid from '../AdminDataGrid';

const AUTH_API_ROOT = String(process.env.REACT_APP_API_URL || "/api").replace(new RegExp("/$"), "");
const AUTH_API_BASE = `${AUTH_API_ROOT}/admin/authority-levels`;
const EMP_MASTER_API_BASE = `${AUTH_API_ROOT}/admin/emp-master-designations`;

function getToken() { return localStorage.getItem("avaada_token"); }
function makeApiFetch() {
  return async (url, opts = {}) => {
    const headers = { "Content-Type": "application/json", ...opts.headers };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(url, { ...opts, headers, credentials: "include" });
    if (!res.ok) throw new Error(await res.text().then(t => t || res.statusText));
    return res.json();
  };
}

const AuthorityLevels = ({ isLight, apiFetch: propApiFetch }) => {
  const fetchFn = useMemo(() => propApiFetch || makeApiFetch(), [propApiFetch]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const bg = isLight ? "#ffffff" : "#0f172a";
  const textMain = isLight ? "#0f172a" : "#f8fafc";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [authResp, empResp] = await Promise.all([fetchFn(AUTH_API_BASE), fetchFn(EMP_MASTER_API_BASE)]);
      const authArr = Array.isArray(authResp) ? authResp : (authResp?.rows || []);
      const empArr = Array.isArray(empResp) ? empResp : (empResp?.rows || []);
      const designations = Array.from(new Set(empArr.map(d => String(d.designation_name || '').trim()).filter(Boolean))).sort();
      const levelMap = new Map(authArr.map(l => [String(l.designation || '').toLowerCase(), l]));
      const ordered = designations.map((d, i) => {
        const lvl = levelMap.get(d.toLowerCase());
        return { id: d, role: d, rank: lvl ? lvl.rank_order : i + 1, levelId: lvl?.id || null };
      }).sort((a, b) => a.rank - b.rank);
      setRows(ordered);
    } catch (e) { console.warn(e.message); setRows([]); }
    finally { setLoading(false); }
  }, [fetchFn]);

  useEffect(() => { load(); }, [load]);

  const persistOrder = async (reordered) => {
    setRows(reordered); setLoading(true);
    try {
      await fetchFn(`${AUTH_API_BASE}/reorder`, {
        method: 'POST',
        body: JSON.stringify({ levels: reordered.map((item) => ({ id: item.levelId, designation: item.role, rank_order: item.rank })) }),
      });
      await load();
    } catch (e) { console.error('[authority] reorder failed:', e.message); await load(); }
    finally { setLoading(false); }
  };

  const moveRow = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const reordered = [...rows];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    persistOrder(reordered.map((row, i) => ({ ...row, rank: i + 1 })));
  };

  const handleRowOrderChange = (params) => {
    const reordered = [...rows];
    const [row] = reordered.splice(params.oldIndex, 1);
    reordered.splice(params.targetIndex, 0, row);
    persistOrder(reordered.map((item, i) => ({ ...item, rank: i + 1 })));
  };

  const columns = [
    { field: 'rank', headerName: 'Rank', width: 80, sortable: false },
    { field: 'role', headerName: 'Role / Designation', flex: 1, sortable: false },
    { field: 'actions', headerName: 'Actions', width: 180, sortable: false, renderCell: (params) => { const index = rows.findIndex((r) => r.id === params.id); return (<div><Button size="small" onClick={() => moveRow(index, -1)} disabled={index <= 0}>↑</Button><Button size="small" onClick={() => moveRow(index, 1)} disabled={index < 0 || index >= rows.length - 1}>↓</Button></div>); } },
  ];

  if (loading && !rows.length) return <CircularProgress />;

  return (
    <Paper style={{ width: '100%', background: bg, padding: 16, boxSizing: 'border-box' }}>
      <Typography variant="h6" style={{ padding: 16, color: textMain }}>Approver Authority (change rank with Actions)</Typography>
      <AdminDataGrid
        rows={rows}
        columns={columns}
        isLight={isLight}
        height={520}
        rowReordering
        disableColumnSorting
        disableColumnMenu
        onRowOrderChange={handleRowOrderChange}
        loading={loading}
        getRowId={(r) => r.id}
      />
    </Paper>
  );
};

export default AuthorityLevels;
